import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Truck, Boxes, ClipboardList, Radio, MapPin, ArrowRightLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";

const Metric = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-2">
    <Icon className={`w-4 h-4 ${accent ? "text-primary-orange" : "text-muted-industrial"}`} />
    <div>
      <div className={`heading text-xl font-black leading-none ${accent ? "text-primary-orange" : "text-ink"}`}>{value}</div>
      <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mt-0.5">{label}</div>
    </div>
  </div>
);

export default function Plants() {
  const nav = useNavigate();
  const { plants, setPlantId } = usePlant();
  const [data, setData] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/plants/overview"),
      api.get("/trucks"),
    ]).then(([o, t]) => { setData(o.data); setTrucks(t.data); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);

  const drillIn = (pid, to) => { setPlantId(pid); nav(to); };

  const moveTruck = async (tid, pid) => {
    setMoving(tid);
    try {
      await api.post(`/trucks/${tid}/transfer`, { plant_id: pid || null });
      setTrucks((prev) => prev.map((t) => (t.id === tid ? { ...t, plant_id: pid || null } : t)));
      toast.success("Truck moved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't move truck"); }
    finally { setMoving(null); }
  };

  const plantName = (pid) => plants.find((p) => p.id === pid)?.name || "— unassigned —";
  const T = data?.totals;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="plants-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> MULTI-PLANT HQ</div>
          <h1 className="heading text-3xl font-black uppercase">Plants <span className="text-primary-orange">Overview</span></h1>
          <div className="text-xs text-muted-industrial mt-1">
            Live roll-up across every plant · {data?.date || "today"}
          </div>
        </div>
        {T && (
          <div className="card-industrial px-5 py-3 flex items-center gap-6" data-testid="plants-org-totals">
            <Metric icon={Boxes} label="m³ today" value={T.delivered_m3} accent />
            <Metric icon={ClipboardList} label="loads" value={T.loads} />
            <Metric icon={Truck} label="trucks live" value={`${T.trucks_active}/${T.trucks_total}`} />
            <Metric icon={Radio} label="jobs on" value={T.jobs_in_progress} />
          </div>
        )}
      </div>

      {loading && !data && (
        <div className="flex items-center gap-2 text-muted-industrial mono text-xs" data-testid="plants-loading"><Loader2 className="w-4 h-4 animate-spin" /> loading plants…</div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(data?.plants || []).map((p) => (
          <div key={p.id} className="card-industrial p-5 flex flex-col gap-4" data-testid={`plant-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="mono text-[10px] uppercase tracking-widest text-primary-orange">{p.code}</div>
                <div className="heading text-lg font-black truncate">{p.name}</div>
                {p.address && <div className="text-xs text-muted-industrial truncate">{p.address}</div>}
              </div>
              {p.trucks_active > 0 && (
                <span className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest text-primary-orange border border-primary-orange px-1.5 py-0.5">
                  <Radio className="w-2.5 h-2.5 pulse-orange" /> live
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-y-4 gap-x-2 border-t border-b border-industrial py-4">
              <Metric icon={Boxes} label="m³ today" value={p.delivered_m3} accent />
              <Metric icon={ClipboardList} label="loads" value={p.loads} />
              <Metric icon={Truck} label="trucks live" value={`${p.trucks_active}/${p.trucks_total}`} />
              <Metric icon={Radio} label="jobs sched/on" value={`${p.jobs_scheduled}/${p.jobs_in_progress}`} />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => drillIn(p.id, "/dispatch")} data-testid={`plant-open-dispatch-${p.id}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 border border-industrial bg-surface px-3 py-2 mono text-[10px] uppercase tracking-widest text-ink hover:border-primary-orange transition">
                <Radio className="w-3.5 h-3.5" /> Dispatch
              </button>
              <button type="button" onClick={() => drillIn(p.id, "/map")} data-testid={`plant-open-map-${p.id}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 border border-industrial bg-surface px-3 py-2 mono text-[10px] uppercase tracking-widest text-ink hover:border-primary-orange transition">
                <MapPin className="w-3.5 h-3.5" /> Map
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Move trucks between plants */}
      <div className="card-industrial p-5" data-testid="plants-fleet-assign">
        <div className="flex items-center gap-2 mb-4">
          <ArrowRightLeft className="w-4 h-4 text-primary-orange" />
          <h2 className="heading text-base font-black uppercase">Move trucks between plants</h2>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {trucks.map((t) => (
            <div key={t.id} className="flex items-center gap-2 border border-industrial px-3 py-2" data-testid={`fleet-assign-row-${t.id}`}>
              <Truck className="w-4 h-4 text-muted-industrial shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="mono text-xs font-bold text-ink truncate">{t.number}</div>
                <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial truncate">{plantName(t.plant_id)}</div>
              </div>
              {moving === t.id ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary-orange" />
              ) : (
                <select
                  value={t.plant_id || ""}
                  onChange={(e) => moveTruck(t.id, e.target.value)}
                  data-testid={`fleet-assign-select-${t.id}`}
                  className="bg-surface border border-industrial px-2 py-1 mono text-[10px] text-ink focus:border-primary-orange outline-none max-w-[48%]"
                >
                  <option value="">Unassigned</option>
                  {plants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
            </div>
          ))}
          {!trucks.length && !loading && <div className="mono text-xs text-muted-industrial">No trucks yet.</div>}
        </div>
      </div>
    </div>
  );
}
